import { useState, useMemo } from 'react';
import { format, isToday, isFuture, isPast /*, startOfDay*/ } from 'date-fns';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import toast from 'react-hot-toast';
import { AppLayout } from '../components/layout';
import { Card, Button, Input, Select } from '../components/ui';
import useServicesStore from '../stores/servicesStore';

interface AppointmentData {
	service: string;
	size: string;
	length: string;
	clientName: string;
	clientEmail: string;
	clientPhone: string;
	date?: Date;
	time?: string;
	staff?: string;
	notes: string;
}

interface SavedAppointment extends AppointmentData {
	id: string;
	price: number;
	duration: number;
	createdAt: Date;
}

type TabType = 'today' | 'future' | 'previous';

// Mock data matching the source HTML - to be replaced with API calls
const generateMockAppointments = (): SavedAppointment[] => {
	const now = Date.now();

	return [
		// Previous appointments
		{
			id: '16',
			clientName: 'Laura Martinez',
			clientEmail: 'laura.martinez@email.com',
			clientPhone: '',
			service: 'Blowout',
			size: 'M',
			length: 'Shoulder',
			date: new Date(now - 604800000), // 7 days ago
			time: '2:00 PM',
			staff: 'Emily Chen',
			notes: '',
			duration: 30,
			price: 50,
			createdAt: new Date(now - 604800000),
		},
		{
			id: '17',
			clientName: 'Daniel Cooper',
			clientEmail: '',
			clientPhone: '(555) 222-3333',
			service: 'Trim',
			size: 'S',
			length: 'Shoulder',
			date: new Date(now - 1209600000), // 14 days ago
			time: '10:00 AM',
			staff: 'Marcus Williams',
			notes: '',
			duration: 30,
			price: 35,
			createdAt: new Date(now - 1209600000),
		},
		{
			id: '18',
			clientName: 'Sophia Turner',
			clientEmail: 'sophia.t@email.com',
			clientPhone: '(555) 444-5555',
			service: 'Full Color',
			size: 'L',
			length: 'Waist',
			date: new Date(now - 2592000000), // 30 days ago
			time: '11:00 AM',
			staff: 'Sarah Johnson',
			notes: '',
			duration: 120,
			price: 150,
			createdAt: new Date(now - 2592000000),
		},
		{
			id: '19',
			clientName: 'Oliver Bennett',
			clientEmail: 'oliver.b@email.com',
			clientPhone: '',
			service: 'Highlights',
			size: 'M',
			length: 'Mid-Back',
			date: new Date(now - 7776000000), // 90 days ago
			time: '3:00 PM',
			staff: 'Lisa Anderson',
			notes: '',
			duration: 90,
			price: 120,
			createdAt: new Date(now - 7776000000),
		},
		{
			id: '20',
			clientName: 'Isabella Reed',
			clientEmail: '',
			clientPhone: '(555) 666-7777',
			service: 'Balayage',
			size: 'XL',
			length: 'Butt',
			date: new Date(now - 31536000000), // 365 days ago
			time: '1:00 PM',
			staff: 'Sarah Johnson',
			notes: '',
			duration: 120,
			price: 180,
			createdAt: new Date(now - 31536000000),
		},
		// Today's appointments
		{
			id: '6',
			clientName: 'Jane Smith',
			clientEmail: 'jane.smith@email.com',
			clientPhone: '(555) 234-5678',
			service: 'Haircut & Style',
			size: 'M',
			length: 'Shoulder',
			date: new Date(),
			time: '9:00 AM',
			staff: 'Emily Chen',
			notes: '',
			duration: 60,
			price: 75,
			createdAt: new Date(),
		},
		{
			id: '7',
			clientName: 'Patricia Johnson',
			clientEmail: 'patricia.j@email.com',
			clientPhone: '(555) 345-6789',
			service: 'Root Touch-Up',
			size: 'L',
			length: 'Mid-Back',
			date: new Date(),
			time: '11:30 AM',
			staff: 'Lisa Anderson',
			notes: 'Cover gray roots, same color as last time',
			duration: 90,
			price: 110,
			createdAt: new Date(),
		},
		{
			id: '8',
			clientName: 'David Wilson',
			clientEmail: '',
			clientPhone: '',
			service: 'Blowout',
			size: 'L',
			length: 'Mid-Back',
			date: new Date(),
			time: '2:00 PM',
			staff: 'Lisa Anderson',
			notes: '',
			duration: 30,
			price: 55,
			createdAt: new Date(),
		},
		{
			id: '9',
			clientName: 'Lisa Thompson',
			clientEmail: '',
			clientPhone: '',
			service: 'Deep Conditioning Treatment',
			size: 'M',
			length: 'Bra',
			date: new Date(),
			time: '4:00 PM',
			staff: 'Emily Chen',
			notes: '',
			duration: 45,
			price: 50,
			createdAt: new Date(),
		},
		// Future appointments
		{
			id: '10',
			clientName: 'Mike Johnson',
			clientEmail: 'mike.johnson@email.com',
			clientPhone: '(555) 456-7890',
			service: 'Full Color',
			size: 'L',
			length: 'Waist',
			date: new Date(now + 86400000), // Tomorrow
			time: '10:00 AM',
			staff: 'Sarah Johnson',
			notes: '',
			duration: 120,
			price: 150,
			createdAt: new Date(),
		},
		{
			id: '11',
			clientName: 'Emily Davis',
			clientEmail: 'emily.d@email.com',
			clientPhone: '',
			service: 'Haircut & Style',
			size: 'S',
			length: 'Shoulder',
			date: new Date(now + 86400000), // Tomorrow
			time: '2:00 PM',
			staff: 'Emily Chen',
			notes: '',
			duration: 60,
			price: 65,
			createdAt: new Date(),
		},
		{
			id: '12',
			clientName: 'Sarah Davis',
			clientEmail: '',
			clientPhone: '',
			service: 'Balayage',
			size: 'XL',
			length: 'Butt',
			date: new Date(now + 172800000), // 2 days from now
			time: '11:00 AM',
			staff: 'Sarah Johnson',
			notes: '',
			duration: 120,
			price: 180,
			createdAt: new Date(),
		},
		{
			id: '13',
			clientName: 'Christopher Lee',
			clientEmail: '',
			clientPhone: '',
			service: 'Trim',
			size: 'XS',
			length: 'Shoulder',
			date: new Date(now + 259200000), // 3 days from now
			time: '3:30 PM',
			staff: 'Marcus Williams',
			notes: '',
			duration: 30,
			price: 30,
			createdAt: new Date(),
		},
		{
			id: '14',
			clientName: 'Michelle Rodriguez',
			clientEmail: '',
			clientPhone: '',
			service: 'Highlights',
			size: 'M',
			length: 'Mid-Back',
			date: new Date(now + 345600000), // 4 days from now
			time: '1:00 PM',
			staff: 'Lisa Anderson',
			notes: '',
			duration: 90,
			price: 120,
			createdAt: new Date(),
		},
		{
			id: '15',
			clientName: 'Brian Anderson',
			clientEmail: '',
			clientPhone: '',
			service: 'Haircut',
			size: 'SM',
			length: 'Shoulder',
			date: new Date(now + 432000000), // 5 days from now
			time: '10:30 AM',
			staff: 'Emily Chen',
			notes: '',
			duration: 45,
			price: 58,
			createdAt: new Date(),
		},
	];
};

const Appointments = () => {
	const { services } = useServicesStore();
	const [step, setStep] = useState(1);
	const [activeTab, setActiveTab] = useState<TabType>('today');
	const [searchTerms, setSearchTerms] = useState({ today: '', future: '', previous: '' });

	// Initialize with mock data - replace with API call: const [savedAppointments, setSavedAppointments] = useState<SavedAppointment[]>([]);
	const [savedAppointments, setSavedAppointments] = useState<SavedAppointment[]>(
		generateMockAppointments()
	);

	const [appointmentData, setAppointmentData] = useState<AppointmentData>({
		service: '',
		size: '',
		length: '',
		clientName: '',
		clientEmail: '',
		clientPhone: '',
		notes: '',
	});

	const timeSlots = [
		'09:00 AM',
		'09:30 AM',
		'10:00 AM',
		'10:30 AM',
		'11:00 AM',
		'11:30 AM',
		'12:00 PM',
		'12:30 PM',
		'01:00 PM',
		'01:30 PM',
		'02:00 PM',
		'02:30 PM',
		'03:00 PM',
		'03:30 PM',
		'04:00 PM',
		'04:30 PM',
		'05:00 PM',
		'05:30 PM',
	];

	const staffMembers = [
		{ id: '1', name: 'Sarah Johnson', specialty: 'Liner', color: '#9333ea' },
		{ id: '2', name: 'Emily Chen', specialty: 'Picker', color: '#3b82f6' },
		{ id: '3', name: 'Marcus Williams', specialty: 'Dropper', color: '#10b981' },
		{ id: '4', name: 'Lisa Anderson', specialty: 'Feeder', color: '#f59e0b' },
	];

	const canProceedToStep2 = appointmentData.service && appointmentData.clientName;
	const canProceedToStep3 = appointmentData.date && appointmentData.time;
	const canProceedToStep4 = appointmentData.staff;

	// Filter appointments by category
	const filteredAppointments = useMemo(() => {
		// const today = startOfDay(new Date());

		const categorized = {
			today: savedAppointments.filter(apt => apt.date && isToday(apt.date)),
			future: savedAppointments.filter(
				apt => apt.date && isFuture(apt.date) && !isToday(apt.date)
			),
			previous: savedAppointments.filter(
				apt => apt.date && isPast(apt.date) && !isToday(apt.date)
			),
		};

		// Apply search filters
		const searchTerm = searchTerms[activeTab].toLowerCase();
		if (searchTerm) {
			return categorized[activeTab].filter(
				apt =>
					apt.clientName.toLowerCase().includes(searchTerm) ||
					apt.clientEmail?.toLowerCase().includes(searchTerm) ||
					apt.clientPhone?.toLowerCase().includes(searchTerm)
			);
		}

		return categorized[activeTab];
	}, [savedAppointments, activeTab, searchTerms]);

	const appointmentCounts = useMemo(() => {
		// const today = startOfDay(new Date());
		return {
			today: savedAppointments.filter(apt => apt.date && isToday(apt.date)).length,
			future: savedAppointments.filter(
				apt => apt.date && isFuture(apt.date) && !isToday(apt.date)
			).length,
			previous: savedAppointments.filter(
				apt => apt.date && isPast(apt.date) && !isToday(apt.date)
			).length,
		};
	}, [savedAppointments]);

	const handleCreateAppointment = () => {
		const createPromise = new Promise(resolve => {
			setTimeout(() => {
				const newAppointment: SavedAppointment = {
					...appointmentData,
					id: `apt-${Date.now()}`,
					price: 150, // Mock price
					duration: 90, // Mock duration
					createdAt: new Date(),
					date: appointmentData.date!,
					time: appointmentData.time!,
					staff: appointmentData.staff!,
				};

				setSavedAppointments(prev => [...prev, newAppointment]);
				resolve(true);
			}, 1000);
		});

		toast.promise(createPromise, {
			loading: 'Creating appointment...',
			success: 'Appointment created successfully!',
			error: 'Failed to create appointment',
		});

		createPromise.then(() => {
			// Reset form
			setAppointmentData({
				service: '',
				size: '',
				length: '',
				clientName: '',
				clientEmail: '',
				clientPhone: '',
				notes: '',
			});
			setStep(1);
		});
	};

	return (
		<AppLayout>
			<h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
				📅 Appointments
			</h1>

			{/* Step Indicator */}
			<div className="flex items-center justify-between mb-6 max-w-4xl overflow-x-auto pb-2">
				{[1, 2, 3, 4].map(stepNum => (
					<div key={stepNum} className="flex items-center flex-1 min-w-0">
						<div
							className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full font-bold text-sm sm:text-base transition-all flex-shrink-0 ${
								stepNum <= step
									? 'bg-blue-600 dark:bg-blue-500 text-white'
									: 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
							}`}
						>
							{stepNum}
						</div>
						<div
							className={`flex-1 h-1 mx-1 sm:mx-2 ${stepNum < step ? 'bg-blue-600 dark:bg-blue-500' : 'bg-gray-200 dark:bg-gray-700'} ${
								stepNum === 4 ? 'hidden' : ''
							}`}
						/>
					</div>
				))}
			</div>

			<Card className="max-w-4xl">
				<h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
					Create an Appointment
				</h2>
				<p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
					Follow the steps below to schedule a new appointment
				</p>

				{/* Step 1: Service Selection */}
				{step === 1 && (
					<div>
						<h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
							Step 1: Select Service & Client
						</h3>
						<div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-6 text-sm text-blue-800 dark:text-blue-300">
							💡 Choose the service and enter client information
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
							<Select
								label="Service Name"
								required
								value={appointmentData.service}
								onChange={e =>
									setAppointmentData({
										...appointmentData,
										service: e.target.value,
									})
								}
							>
								<option value="">Select Service</option>
								{services.map(service => (
									<option key={service.id} value={service.name}>
										{service.name}
									</option>
								))}
							</Select>

							<Input
								label="Client Name"
								required
								placeholder="Enter client name"
								value={appointmentData.clientName}
								onChange={e =>
									setAppointmentData({
										...appointmentData,
										clientName: e.target.value,
									})
								}
							/>

							<Input
								type="email"
								label="Client Email"
								placeholder="Enter client email"
								value={appointmentData.clientEmail}
								onChange={e =>
									setAppointmentData({
										...appointmentData,
										clientEmail: e.target.value,
									})
								}
							/>

							<Input
								type="tel"
								label="Client Phone"
								placeholder="Enter phone number"
								value={appointmentData.clientPhone}
								onChange={e =>
									setAppointmentData({
										...appointmentData,
										clientPhone: e.target.value,
									})
								}
							/>
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
							<Select
								label="Size"
								value={appointmentData.size}
								onChange={e =>
									setAppointmentData({ ...appointmentData, size: e.target.value })
								}
							>
								<option value="">Select Size</option>
								<option value="S">S - Small</option>
								<option value="M">M - Medium</option>
								<option value="L">L - Large</option>
								<option value="XL">XL - Extra Large</option>
							</Select>

							<Select
								label="Length"
								value={appointmentData.length}
								onChange={e =>
									setAppointmentData({
										...appointmentData,
										length: e.target.value,
									})
								}
							>
								<option value="">Select Length</option>
								<option value="Shoulder">Shoulder</option>
								<option value="Bra">Bra</option>
								<option value="Mid-Back">Mid-Back</option>
								<option value="Waist">Waist</option>
							</Select>
						</div>

						<div className="mb-6">
							<label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
								Notes
							</label>
							<textarea
								className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-blue-600 dark:focus:border-blue-400 focus:ring-3 focus:ring-blue-100 dark:focus:ring-blue-900"
								rows={3}
								placeholder="Enter any special notes or requirements"
								value={appointmentData.notes}
								onChange={e =>
									setAppointmentData({
										...appointmentData,
										notes: e.target.value,
									})
								}
							/>
						</div>

						<Button
							variant="primary"
							onClick={() => setStep(2)}
							disabled={!canProceedToStep2}
						>
							Next: Choose Date & Time →
						</Button>
					</div>
				)}

				{/* Step 2: Date & Time Selection */}
				{step === 2 && (
					<div>
						<h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
							Step 2: Choose Date & Time
						</h3>
						<div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-6 text-sm text-blue-800 dark:text-blue-300">
							📅 Select a date and time slot for the appointment
						</div>

						<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mb-6">
							<div>
								<h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
									Select Date
								</h4>
								<div className="max-w-full overflow-x-auto">
									<DayPicker
										mode="single"
										selected={appointmentData.date}
										onSelect={date =>
											setAppointmentData({ ...appointmentData, date })
										}
										disabled={{ before: new Date() }}
										className="border border-gray-300 dark:border-gray-600 rounded-lg p-3 bg-white dark:bg-gray-800"
									/>
								</div>
							</div>

							<div>
								<h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
									Available Time Slots
								</h4>
								<div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 gap-2 md:gap-3 max-h-96 overflow-y-auto">
									{timeSlots.map(time => (
										<button
											key={time}
											onClick={() =>
												setAppointmentData({ ...appointmentData, time })
											}
											className={`px-3 md:px-4 py-2 md:py-3 rounded-lg border-2 font-medium text-xs sm:text-sm transition-all ${
												appointmentData.time === time
													? 'border-blue-600 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
													: 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10'
											}`}
										>
											{time}
										</button>
									))}
								</div>
							</div>
						</div>

						<div className="flex flex-col sm:flex-row gap-3">
							<Button variant="secondary" onClick={() => setStep(1)}>
								← Back
							</Button>
							<Button
								variant="primary"
								onClick={() => setStep(3)}
								disabled={!canProceedToStep3}
							>
								Next: Assign Staff →
							</Button>
						</div>
					</div>
				)}

				{/* Step 3: Staff Selection */}
				{step === 3 && (
					<div>
						<h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
							Step 3: Assign Staff
						</h3>
						<div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-6 text-sm text-blue-800 dark:text-blue-300">
							👥 Select a staff member for this appointment
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
							{staffMembers.map(staff => (
								<div
									key={staff.id}
									onClick={() =>
										setAppointmentData({
											...appointmentData,
											staff: staff.name,
										})
									}
									className={`p-5 rounded-lg border-2 cursor-pointer transition-all ${
										appointmentData.staff === staff.name
											? 'border-green-600 dark:border-green-400 bg-green-50 dark:bg-green-900/20'
											: 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-md'
									}`}
								>
									<div className="flex items-center gap-3">
										<div className="w-12 h-12 rounded-full bg-blue-600 dark:bg-blue-500 text-white flex items-center justify-center font-bold">
											{staff.name
												.split(' ')
												.map(n => n[0])
												.join('')}
										</div>
										<div>
											<h4 className="font-bold text-gray-900 dark:text-gray-100">
												{staff.name}
											</h4>
											<p className="text-sm text-gray-600 dark:text-gray-400">
												{staff.specialty}
											</p>
										</div>
									</div>
								</div>
							))}
						</div>

						<div className="flex gap-3">
							<Button variant="secondary" onClick={() => setStep(2)}>
								← Back
							</Button>
							<Button
								variant="primary"
								onClick={() => setStep(4)}
								disabled={!canProceedToStep4}
							>
								Next: Confirm →
							</Button>
						</div>
					</div>
				)}

				{/* Step 4: Confirmation */}
				{step === 4 && (
					<div>
						<h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
							Step 4: Confirm Appointment
						</h3>
						<div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-6 text-sm text-blue-800 dark:text-blue-300">
							✅ Review the appointment details and confirm
						</div>

						<div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 md:p-6 mb-6 space-y-3">
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
								<div>
									<span className="font-semibold text-gray-700 dark:text-gray-300">
										Service:
									</span>
									<p className="text-gray-900 dark:text-gray-100">
										{appointmentData.service}
									</p>
								</div>
								<div>
									<span className="font-semibold text-gray-700 dark:text-gray-300">
										Client:
									</span>
									<p className="text-gray-900 dark:text-gray-100">
										{appointmentData.clientName}
									</p>
								</div>
								<div>
									<span className="font-semibold text-gray-700 dark:text-gray-300">
										Date:
									</span>
									<p className="text-gray-900 dark:text-gray-100">
										{appointmentData.date
											? format(appointmentData.date, 'PPP')
											: 'N/A'}
									</p>
								</div>
								<div>
									<span className="font-semibold text-gray-700 dark:text-gray-300">
										Time:
									</span>
									<p className="text-gray-900 dark:text-gray-100">
										{appointmentData.time}
									</p>
								</div>
								<div>
									<span className="font-semibold text-gray-700 dark:text-gray-300">
										Staff:
									</span>
									<p className="text-gray-900 dark:text-gray-100">
										{appointmentData.staff}
									</p>
								</div>
								{appointmentData.size && (
									<div>
										<span className="font-semibold text-gray-700 dark:text-gray-300">
											Size:
										</span>
										<p className="text-gray-900 dark:text-gray-100">
											{appointmentData.size}
										</p>
									</div>
								)}
								{appointmentData.length && (
									<div>
										<span className="font-semibold text-gray-700 dark:text-gray-300">
											Length:
										</span>
										<p className="text-gray-900 dark:text-gray-100">
											{appointmentData.length}
										</p>
									</div>
								)}
								{appointmentData.clientEmail && (
									<div>
										<span className="font-semibold text-gray-700 dark:text-gray-300">
											Email:
										</span>
										<p className="text-gray-900 dark:text-gray-100">
											{appointmentData.clientEmail}
										</p>
									</div>
								)}
								{appointmentData.clientPhone && (
									<div>
										<span className="font-semibold text-gray-700 dark:text-gray-300">
											Phone:
										</span>
										<p className="text-gray-900 dark:text-gray-100">
											{appointmentData.clientPhone}
										</p>
									</div>
								)}
							</div>
							{appointmentData.notes && (
								<div>
									<span className="font-semibold text-gray-700 dark:text-gray-300">
										Notes:
									</span>
									<p className="text-gray-900 dark:text-gray-100">
										{appointmentData.notes}
									</p>
								</div>
							)}
						</div>

						<div className="flex gap-3">
							<Button variant="secondary" onClick={() => setStep(3)}>
								← Back
							</Button>
							<Button variant="success" onClick={handleCreateAppointment}>
								Confirm & Create Appointment
							</Button>
						</div>
					</div>
				)}
			</Card>

			{/* All Appointments Section with Tabs */}
			<Card className="mt-6">
				<h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
					All Appointments
				</h2>

				{/* Tabs */}
				<div className="border-b border-gray-200 dark:border-gray-700 mb-6 overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
					<div className="flex gap-2 min-w-max md:min-w-0">
						<button
							onClick={() => setActiveTab('today')}
							className={`px-3 sm:px-4 py-2 font-medium text-xs sm:text-sm rounded-t-lg transition-colors relative whitespace-nowrap ${
								activeTab === 'today'
									? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
									: 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800'
							}`}
						>
							⭐ Today
							<span className="ml-1 sm:ml-2 px-1.5 sm:px-2 py-0.5 text-xs rounded-full bg-blue-600 dark:bg-blue-500 text-white">
								{appointmentCounts.today}
							</span>
						</button>
						<button
							onClick={() => setActiveTab('future')}
							className={`px-3 sm:px-4 py-2 font-medium text-xs sm:text-sm rounded-t-lg transition-colors relative whitespace-nowrap ${
								activeTab === 'future'
									? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
									: 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800'
							}`}
						>
							📅 Future
							<span className="ml-1 sm:ml-2 px-1.5 sm:px-2 py-0.5 text-xs rounded-full bg-blue-600 dark:bg-blue-500 text-white">
								{appointmentCounts.future}
							</span>
						</button>
						<button
							onClick={() => setActiveTab('previous')}
							className={`px-3 sm:px-4 py-2 font-medium text-xs sm:text-sm rounded-t-lg transition-colors relative whitespace-nowrap ${
								activeTab === 'previous'
									? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
									: 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800'
							}`}
						>
							📜 Previous
							<span className="ml-1 sm:ml-2 px-1.5 sm:px-2 py-0.5 text-xs rounded-full bg-blue-600 dark:bg-blue-500 text-white">
								{appointmentCounts.previous}
							</span>
						</button>
					</div>
				</div>

				{/* Search Bar */}
				<div className="mb-6">
					<Input
						type="text"
						placeholder="🔍 Search Client's Name / Phone Number / Email"
						value={searchTerms[activeTab]}
						onChange={e =>
							setSearchTerms(prev => ({ ...prev, [activeTab]: e.target.value }))
						}
						className="w-full sm:max-w-md"
					/>
				</div>

				{/* Appointments Grid */}
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
					{filteredAppointments.length === 0 ? (
						<div className="col-span-full text-center py-12">
							<div className="text-6xl mb-4 opacity-30">
								{activeTab === 'today'
									? '📅'
									: activeTab === 'future'
										? '📆'
										: '📜'}
							</div>
							<h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
								{searchTerms[activeTab]
									? 'No appointments found matching your search'
									: activeTab === 'today'
										? 'No appointments scheduled for today'
										: activeTab === 'future'
											? 'No future appointments'
											: 'No previous appointments'}
							</h3>
						</div>
					) : (
						filteredAppointments.map(apt => (
							<div
								key={apt.id}
								className={`p-4 rounded-lg border-l-4 transition-all hover:shadow-md ${
									activeTab === 'today'
										? 'bg-blue-50 dark:bg-blue-900/20 border-blue-600 dark:border-blue-400'
										: activeTab === 'future'
											? 'bg-green-50 dark:bg-green-900/20 border-green-600 dark:border-green-400'
											: 'bg-gray-50 dark:bg-gray-800 border-gray-400 dark:border-gray-600'
								}`}
							>
								{/* Header */}
								<div className="flex justify-between items-start mb-3">
									<div className="flex items-center gap-2">
										<span className="text-2xl">
											{activeTab === 'today'
												? '🕐'
												: activeTab === 'future'
													? '📅'
													: '✓'}
										</span>
										<div>
											<div className="font-bold text-gray-900 dark:text-gray-100">
												{apt.time}
											</div>
											<div className="text-xs text-gray-600 dark:text-gray-400">
												{apt.date ? format(apt.date, 'EEE, MMM d') : 'N/A'}
											</div>
										</div>
									</div>
									<div className="text-right">
										<div className="font-bold text-green-600 dark:text-green-400">
											${apt.price.toFixed(2)}
										</div>
										<div className="text-xs text-gray-600 dark:text-gray-400">
											{apt.duration} mins
										</div>
									</div>
								</div>

								{/* Client Info */}
								<div className="mb-3">
									<div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
										👤 {apt.clientName}
									</div>
									{apt.clientEmail && (
										<div className="text-xs text-gray-600 dark:text-gray-400">
											📧 {apt.clientEmail}
										</div>
									)}
									{apt.clientPhone && (
										<div className="text-xs text-gray-600 dark:text-gray-400">
											📞 {apt.clientPhone}
										</div>
									)}
								</div>

								{/* Service Tags */}
								<div className="flex flex-wrap gap-2 mb-3">
									<span className="px-2 py-1 text-xs font-medium rounded-lg bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200">
										{apt.service}
									</span>
									{apt.size && (
										<span className="px-2 py-1 text-xs font-medium rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
											{apt.size}
										</span>
									)}
									{apt.length && (
										<span className="px-2 py-1 text-xs font-medium rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
											{apt.length}
										</span>
									)}
								</div>

								{/* Notes */}
								{apt.notes && (
									<div className="mb-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded text-xs text-gray-700 dark:text-gray-300">
										<div className="font-semibold mb-1">📝 Notes:</div>
										{apt.notes}
									</div>
								)}

								{/* Staff */}
								<div className="pt-3 border-t border-gray-200 dark:border-gray-700">
									<div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">
										Staff Member:
									</div>
									<div className="flex items-center gap-2">
										<div className="w-8 h-8 rounded-full bg-blue-600 dark:bg-blue-500 text-white flex items-center justify-center text-xs font-bold">
											{apt.staff
												?.split(' ')
												.map(n => n[0])
												.join('')}
										</div>
										<div className="text-sm font-medium text-gray-900 dark:text-gray-100">
											{apt.staff}
										</div>
									</div>
								</div>
							</div>
						))
					)}
				</div>
			</Card>

			<div className="mt-6 text-sm text-gray-500 dark:text-gray-400 text-center">
				© Beauty Business Portal
			</div>
		</AppLayout>
	);
};

export default Appointments;
